import { useMutation, useQueryClient } from "@tanstack/react-query";
import { setInitialMetricValue } from "../../repositories/SharedEqpRepository";

interface SetInitialMetricValueParams {
  partId: number;
  newValue: number;
  applyToChildren: boolean;
  userId: number | null;
}

/**
 * Shared mutation for setting the initial metric (partial heat count) on a part.
 *
 * @param invalidateKeys - Explicit list of query key prefixes to invalidate on success.
 *   Each entry is passed to `invalidateQueries({ queryKey })` as a prefix match.
 *   Example: `[["roof-tree"], ["roof-details"]]`
 */
export const useSetInitialMetricValueMutation = (
  invalidateKeys: readonly (readonly string[])[],
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: SetInitialMetricValueParams) => {
      const resp = await setInitialMetricValue(
        params.partId,
        params.newValue,
        params.applyToChildren,
        params.userId,
      );
      if (!resp.ok)
        throw new Error(resp.message ?? "Failed to set initial metric value");

      return resp;
    },
    onSuccess: () => {
      invalidateKeys.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: [...key] });
      });
    },
  });
};
